/**
 * Theme store — the single source of truth for everything visual.
 *
 * Three concerns live here, intentionally collapsed into one store so
 * there is exactly one DOM applier and zero ordering races:
 *   1. Light/dark mode (`themeMode` -> resolves to `isDark`)
 *   2. Status-driven accent color (driven by the user's primary agent)
 *   3. Font scale
 *
 * The store DOES NOT touch the DOM directly. The runtime in
 * `./applyTheme.ts` subscribes once at module load and is the only
 * place that writes CSS variables, classes, or inline styles.
 *
 * This separation is what fixes the "theme variable lost on route
 * change" bug: there is no longer a React effect that can be skipped
 * by a stale dep array, no inline `root.style.setProperty` that can be
 * stomped by another module, and no second source of truth.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AgentStatus } from '../types'

export type ThemeMode = 'light' | 'dark' | 'system'
export type FontSize = 'small' | 'standard' | 'large' | 'extraLarge'

export const FONT_SIZE_SCALES: Record<FontSize, number> = {
  small: 0.875,
  standard: 1,
  large: 1.125,
  extraLarge: 1.25,
}

/** UI metadata for the font-size picker. `labelKey` is an i18n key. */
export const FONT_SIZE_OPTIONS: Record<
  FontSize,
  { labelKey: string; scale: number }
> = {
  small: { labelKey: 'settingsGeneral.fontSizeSmall', scale: FONT_SIZE_SCALES.small },
  standard: { labelKey: 'settingsGeneral.fontSizeStandard', scale: FONT_SIZE_SCALES.standard },
  large: { labelKey: 'settingsGeneral.fontSizeLarge', scale: FONT_SIZE_SCALES.large },
  extraLarge: { labelKey: 'settingsGeneral.fontSizeExtraLarge', scale: FONT_SIZE_SCALES.extraLarge },
}

interface ThemeState {
  /** User's chosen mode. `system` follows the OS preference live. */
  themeMode: ThemeMode
  /** Whether the accent color tracks the agent's status. */
  statusThemeEnabled: boolean
  /** Current agent status, fed in by the agent store. */
  agentStatus: AgentStatus | 'none'
  /** Font scale token. */
  fontSize: FontSize

  setThemeMode: (mode: ThemeMode) => void
  toggleDarkMode: () => void
  setStatusThemeEnabled: (enabled: boolean) => void
  setAgentStatus: (status: AgentStatus | 'none') => void
  setFontSize: (size: FontSize) => void
}

const isBrowser = typeof window !== 'undefined'

const getSystemPrefersDark = (): boolean =>
  isBrowser && window.matchMedia('(prefers-color-scheme: dark)').matches

/** Resolve `themeMode` to a concrete light/dark boolean. */
export const resolveIsDark = (mode: ThemeMode): boolean => {
  if (mode === 'system') return getSystemPrefersDark()
  return mode === 'dark'
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      // Default to explicit light. Following the OS preference by
      // default surprised users on dark-mode machines: every fresh
      // load looked like the theme had "reverted to dark". Users who
      // actually want OS-following can pick `system` from settings.
      themeMode: 'light',
      statusThemeEnabled: false,
      agentStatus: 'none',
      fontSize: 'standard',

      setThemeMode: (mode) => set({ themeMode: mode }),

      toggleDarkMode: () => {
        const isDark = resolveIsDark(get().themeMode)
        set({ themeMode: isDark ? 'light' : 'dark' })
      },

      setStatusThemeEnabled: (enabled) => set({ statusThemeEnabled: enabled }),

      setAgentStatus: (status) => set({ agentStatus: status }),

      setFontSize: (size) => set({ fontSize: size }),
    }),
    {
      name: 'theme-storage',
      version: 2,
      // Only the user's choices are persisted. `agentStatus` is derived
      // at runtime from the agent store and must not be restored.
      partialize: (state) => ({
        themeMode: state.themeMode,
        statusThemeEnabled: state.statusThemeEnabled,
        fontSize: state.fontSize,
      }),
      // Migration v1 → v2: earlier builds defaulted (and persisted)
      // `themeMode: 'system'`, which on dark-mode laptops made every
      // fresh load look like the theme had silently flipped to dark.
      // Rewrite any leftover 'system' to an explicit 'light'. Users
      // who want OS-following can re-enable it from settings.
      migrate: (persisted, _version) => {
        const state = (persisted ?? {}) as Partial<ThemeState>
        if (state.themeMode === 'system') {
          state.themeMode = 'light'
        }
        return state as ThemeState
      },
    },
  ),
)

/** True iff the resolved theme mode is dark. Selector helper. */
export const useIsDarkMode = (): boolean => {
  const mode = useThemeStore((s) => s.themeMode)
  return resolveIsDark(mode)
}
